import React, { useEffect, useMemo, useState } from "react";
import { Table, Spin } from "antd";
import "bootstrap/dist/css/bootstrap.css";
import SidebarNav from "../sidebar";
import { Link } from "react-router-dom";
import { getBalanceComparison } from "../../../service/api";
import { toast } from "react-toastify";
import FilterPanel from "../commoncomponent/FilterPanel";
import TableToolbar from "../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../hooks/useTableSettings";
import { sanitizeInput } from "../../../config/validation";
import { triggerDownload } from "../../../config/downloadUtils";

const ALL_COLUMNS = [
  {
    key: "coin",
    title: "Coin",
    defaultVisible: true,
    render: (row) => {
      const content = (
        <div className="d-flex align-items-center gap-2">
          {row?.icon && (
            <img
              src={row.icon}
              alt={row.symbol}
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "12px",
                objectFit: "cover",
              }}
            />
          )}
          <div>
            <div style={{ fontWeight: 600 }}>{row?.name}</div>
            <div style={{ fontSize: "12px", color: "#888" }}>{row?.symbol}</div>
          </div>
        </div>
      );
      return row?._id ? (
        <Link
          to={`/admin/coin-detail/${row._id}`}
          style={{ color: "inherit", textDecoration: "none" }}
        >
          {content}
        </Link>
      ) : (
        content
      );
    },
  },
  {
    key: "symbol",
    title: "Symbol",
    defaultVisible: false,
    render: (r) => r?.symbol || "-",
  },
  {
    key: "paraaBalance",
    title: "Paraa Balance",
    defaultVisible: true,
    render: (r) => <span>{Number(r?.paraaBalance || 0).toFixed(8)}</span>,
  },
  {
    key: "dcxBalance",
    title: "CoinDCX Balance",
    defaultVisible: true,
    render: (r) => <span>{Number(r?.dcxBalance || 0).toFixed(8)}</span>,
  },
  {
    key: "dcxDifference",
    title: "Diff (CoinDCX − Paraa)",
    defaultVisible: true,
    render: (r) => {
      const val = Number(r?.dcxDifference || 0);
      return (
        <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f", fontWeight: 600 }}>
          {val >= 0 ? "+" : ""}{val.toFixed(8)}
        </span>
      );
    },
  },
  {
    key: "kucoinBalance",
    title: "KuCoin Balance",
    defaultVisible: true,
    render: (r) => <span>{Number(r?.kucoinBalance || 0).toFixed(8)}</span>,
  },
  {
    key: "kucoinDifference",
    title: "Diff (KuCoin − Paraa)",
    defaultVisible: true,
    render: (r) => {
      const val = Number(r?.kucoinDifference || 0);
      return (
        <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f", fontWeight: 600 }}>
          {val >= 0 ? "+" : ""}{val.toFixed(8)}
        </span>
      );
    },
  },
];

const BalanceComparison = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "balance-comparison-list",
    allColumns: ALL_COLUMNS,
  });

  const fetchData = () => {
    setLoading(true);
    getBalanceComparison()
      .then((res) => {
        if (res?.status) {
          setData(res?.data || []);
        }
        setLoading(false);
      })
      .catch((e) => {
        toast.error(
          e?.response?.data?.message || "Failed to fetch balance comparison",
          { toastId: 'balance-comparison-fetch-error' }
        );
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Reset to page 1 whenever filters or page size change.
  useEffect(() => {
    setPage(1);
  }, [pageSize, searchTerm]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (!searchTerm) return data;
    const q = searchTerm.trim().toLowerCase();
    return data.filter(
      (r) =>
        (r?.name && r.name.toLowerCase().includes(q)) ||
        (r?.symbol && r.symbol.toLowerCase().includes(q))
    );
  }, [data, searchTerm]);

  const total = filteredData.length;

  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const handleDownload = (format) => {
    if (!filteredData?.length) { toast.warning("Nothing to download", { toastId: 'nothing-to-download' }); return; }
    const cols = ALL_COLUMNS?.filter((c) => visibleColumnKeys?.includes(c?.key));
    triggerDownload(format, filteredData, cols, "balance-comparison");
  };

  const columns = [...dynamicColumns];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-7 col-auto">
                <h3 className="page-title">Balance Comparison</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/admin">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">
                    Balance Comparison
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="list-layout">
            <div className="list-layout__main">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">
                    Coin Balance: ParaaCrypto Users vs Exchanges (CoinDCX + KuCoin)
                  </h4>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={fetchData}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Refresh"}
                  </button>
                </div>
                <div className="card-body">
                  <TableToolbar
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                    total={total}
                    page={page}
                    visibleColumnKeys={visibleColumnKeys}
                    allColumns={ALL_COLUMNS}
                    toggleColumn={toggleColumn}
                    resetColumns={resetColumns}
                    onDownload={handleDownload}
                  />
                  {loading && data.length === 0 ? (
                    <div className="text-center py-5">
                      <Spin size="large" />
                    </div>
                  ) : (
                    <Table
                      columns={columns}
                      dataSource={pagedData}
                      rowKey={(r) => r?.symbol || r?._id || r?.name}
                      pagination={{
                        current: page,
                        pageSize: pageSize,
                        total: total,
                        onChange: (p) => setPage(p),
                        showSizeChanger: false,
                      }}
                      loading={loading}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="list-layout__filters">
              <FilterPanel
                count={total}
                onClear={() => {
                  setSearchTerm("");
                }}
              >
                <FilterPanel.Field label="Search">
                  <input
                    type="text"
                    placeholder="Coin name or symbol"
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerm(sanitizeInput(e.target.value))
                    }
                  />
                </FilterPanel.Field>
              </FilterPanel>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BalanceComparison;
